import React, {useEffect, useRef, useState} from 'react'
import styles from './UIGameLayout.module.scss'
import {UIGameEnd} from '../UIGameEnd'
import {UIGameFeeds} from '../UIGameFeeds/UIGameFeeds'
import {UIGamePause} from '../UIGamePause/UIGamePause'
import {UIGameUserSettings} from '../UIGameUserSettings'
import {UIGameHelpButtons} from '../UIGameHelpButtons/UIGameHelpButtons'
import {StorageWrapper} from '../../utils/storage'
import {UIGameSpectators} from '../UIGameSpectators/UIGameSpectators'
import {UIGameCardAnimator} from '../UIGameCardAnimator/UIGameCardAnimator'
import {UIGameVoteContainer} from '../UIGameVote/UIGameVoteContainer'
import {UIGameEndReturnToStats} from '../UIGameEnd/UIGameEndReturnToStats/UIGameEndReturnToStats'
import {UIGameTradeOffers} from '../UIGameTradeOffer/UIGameTradeOffers'
import {UIGameAchievementContainer} from '../UIGameAchievement/UIGameAchievementContainer'
import {ModeSettingEnum} from '@colonist/types'
import {UIGameFeedsPortraitVisibility} from '../UIGameFeeds/UIGameFeedsPortrait'
import {UIGameBank} from '../UIGameBank'
import {UIGamePlayerInformationContainer} from '../UIGamePlayerInformation/UIGamePlayerInformationContainer'
import {UIGameCurrentStatusContainer} from '../UIGameCurrentStatus'
import {useGameStore} from '../../store/GameStoreContext'
import {UIGameTimerBarContainer} from '../UIGameTimerBar/UIGameTimerBarContainer'
import {UIGameBarbarianInvasionTrackerContainer} from '../UIGameBarbarianInvasionTracker/UIGameBarbarianInvasionTrackerContainer'
import {UIGameReplayControlsContainer} from '../UIGameReplayControls/UIGameReplayControlsContainer'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import {usePortraitPlayerInformationContainerPosition} from '../../hooks/usePortraitPlayerInformationAreaRect'
import clsx from 'clsx'
import {UIGameBeginnerHint} from '../UIGameBeginnerHint'
import {UIGameBottomLandscape, UIGameBottomPortrait} from '../UIGameBottom'
import {UIGamePortraitTop} from '../UIGamePortraitTop'
import {useUIGameLandscapeResponsive} from './useUIGameResponsive'

type UIGameOrientation = 'portrait' | 'landscape'

interface UIGameLayoutProps {
    orientation: UIGameOrientation
    isChatDefaultMinimized: boolean
    isChatEnabled: boolean
    onSendChat: (message: string) => void
    onSetChatVisible: (visible: boolean) => void
    onSetLogVisible: (visible: boolean) => void
    initialCanResign: boolean
}

export const UIGameLayout = (props: UIGameLayoutProps) => {
    if(props.orientation === 'portrait') return <UIGameLayoutPortrait {...props} />
    else return <UIGameLayoutLandscape {...props} />
}

export const UIGameLayoutLandscape = ({
    isChatDefaultMinimized,
    isChatEnabled,
    onSendChat,
    initialCanResign,
}: UIGameLayoutProps) => {
    const onClickLogForReplay = useGameStore((state) => state.gameReplay.replayControls?.onClickLog)
    const onClickLog = onClickLogForReplay ?? (() => void 0)
    const isReplay = useIsReplay()
    const containerRef = useRef<HTMLDivElement>(null)
    const containerFontSize = useUIGameLandscapeResponsive(containerRef)

    return (
        <div
            ref={containerRef}
            className={clsx(styles.containerLandscape)}
            style={{
                '--container-font-size': `${containerFontSize}rem`,
                gridTemplateColumns: `
                                    [start]
                                    auto
                                    [help-buttons-end] 
                                    1fr
                                    [game-feeds]
                                    19em
                                    [end]`,
                gridTemplateRows: `
                                    [start]
                                    1fr
                                    [game-feeds-end]
                                    auto
                                    [bank-end]
                                    21.25em
                                    [end]`,
            } as React.CSSProperties}>
            <div className={styles.gameTopLeftContainer}>
                <div className={styles.gameHelpButtonsContainer}>
                    <UIGameHelpButtons className={styles.gameHelpButtonsLayer} initialCanResign={initialCanResign} />
                </div>
                <div className={styles.gameSpectatorsContainer}>
                    <UIGameSpectators />
                </div>
                <div className={styles.gameBarbarianInvasionTrackerContainer}>
                    <UIGameBarbarianInvasionTrackerContainer />
                </div>
            </div>
            <div className={styles.gameSettingsContainer}>
                <UIGameUserSettings />
            </div>
            <div className={styles.gameEndContainer}>
                <UIGameEnd />
            </div>
            <div className={styles.gamePauseContainer}>
                <UIGamePause />
            </div>
            <div className={styles.gameTradeOffersContainer}>
                <UIGameTradeOffers className={styles.gameTradeOffersWrapper}/>
            </div>
            <div className={styles.gameBottomContainer}>
                <UIGameBottomLandscape />
            </div>
            <div className={styles.gameAchievementContainer}>
                <UIGameAchievementContainer />
            </div>
            <UIGameFeeds
                className={styles.gameFeedsContainer}
                isChatDefaultMinimized={isChatDefaultMinimized}
                isChatEnabled={isChatEnabled}
                onClickLog={onClickLog}
                onSendChat={onSendChat}/>
            <UIGameBank
                className={styles.gameBankContainer}
                isLandscape={true}
            />
            <UIGamePlayerInformationContainer className={styles.gamePlayerInformationContainer} />
            <div className={styles.gameReplayControlsContainer}>
                {isReplay === true && <div className={styles.gameReplayControlsContainerInner}>
                    <UIGameReplayControlsContainer useMinimalControls={false} />
                </div>}
                <UIGameEndReturnToStats text="strings:game.endGame.cornerButton.openStats" />
            </div>
            <div className={styles.gameCardAnimatorContainer}>
                <UIGameCardAnimator />
            </div>
            <UIGameBeginnerHint/>
        </div>
    )
}

export const UIGameLayoutPortrait = ({
    onSendChat,
    onSetChatVisible,
    onSetLogVisible,
    initialCanResign,
}: UIGameLayoutProps) => {
    const safeAreaTop = StorageWrapper.getStatusBarHeight() ?? 0
    const safeAreaBottom = (StorageWrapper.getSafeAreaInsetBottom() ?? 0) * 0.5
    const isReplay = useIsReplay()
    const modeSetting = useGameStore((state) => state.gameSettings.modeSetting)
    const isBeginnerMode = modeSetting === ModeSettingEnum.BeginnerModeClassic4P
    const [feedsPortraitVisibility, setFeedsPortraitVisibility] = useState<UIGameFeedsPortraitVisibility>('none')
    const onClickLogForReplay = useGameStore((state) => state.gameReplay.replayControls?.onClickLog)
    const onClickLog = (index: number) => onClickLogForReplay?.(index)

    usePortraitPlayerInformationContainerPosition()
    const playerInformationContainerSize =
        useGameStore((state) => state.gamePortraitPlayerInformationLayout.playerInformationContainerSize)
        ?? {width: 0, height: 0}

    useEffect(() => {
        if(feedsPortraitVisibility === 'none') {
            onSetChatVisible(false)
            onSetLogVisible(false)
        } else if(feedsPortraitVisibility === 'chat') {
            onSetChatVisible(true)
        } else if(feedsPortraitVisibility === 'log') {
            onSetLogVisible(true)
        }
    }, [feedsPortraitVisibility])

    return (
        <div
            className={styles.containerPortrait}
            style={{
                gridTemplateColumns: '1fr',
                gridTemplateRows: `
                                    [start]
                                    ${safeAreaTop}px
                                    [safe-area-top-end]
                                    min-content
                                    [help-buttons-end]
                                    min-content
                                    [action-status-end]
                                    ${playerInformationContainerSize.height}px
                                    [player-information-container-end]
                                    1fr
                                    [bottom-start]
                                    auto
                                    [timer-bar-start]
                                    auto
                                    [safe-area-bottom-start]
                                    ${safeAreaBottom}px
                                    [end]`,
            }}>
            <div className={styles.gameHelpBackground}></div>
            <UIGamePortraitTop initialCanResign={initialCanResign} className={styles.gameTopContainer} />
            <div className={styles.gameCurrentStatusContainer} data-game-current-status-container>
                <UIGameCurrentStatusContainer backgroundColor='blue' className={styles.gameCurrentStatusWrapper} />
            </div>
            {isReplay === false && <div className={styles.gameTradeOffersContainer}>
                <UIGameTradeOffers className={styles.gameTradeOffersWrapper} disableTooltips/>
            </div>}
            {isReplay === false && isBeginnerMode === false && <div className={styles.gameTimerBarContainer}>
                <UIGameTimerBarContainer />
            </div>}
            <div className={styles.gameSettingsContainer}>
                <UIGameUserSettings
                    enabledTabs={['game-info', 'display', 'audio']}
                    isNestedContentEnabled={true} />
            </div>
            <div className={styles.gameEndContainer}>
                <UIGameEnd />
            </div>
            <div className={styles.gamePauseContainer}>
                <UIGamePause />
            </div>
            <div className={styles.gameAchievementContainer}>
                <UIGameAchievementContainer />
            </div>
            <div className={styles.gameBarbarianInvasionTrackerContainer}>
                <UIGameBarbarianInvasionTrackerContainer />
            </div>
            <div className={styles.gameCardAnimatorContainer}>
                <UIGameCardAnimator />
            </div>
            <div className={styles.gameVoteContainer}>
                <UIGameVoteContainer />
            </div>
            <div className={styles.gameBottomContainer}>
                <UIGameBottomPortrait
                    onClickLog={onClickLog}
                    onSendChat={onSendChat}
                    feedsPortraitVisibility={feedsPortraitVisibility}
                    setFeedsPortraitVisibility={setFeedsPortraitVisibility} />
            </div>
            <UIGameBeginnerHint />
        </div>
    )
}
